import { Range } from "@tiptap/core";
import { ResolvedPos, Node } from "@tiptap/pm/model";
import { IMentionAttributes } from "../utils";

export type DueDateSuggestionMatch = {
  range: Range;
  query: string;
  text: string;
  mention: {
    node: Node;
    range: Range;
  };
};

export function findDueDateSuggestionMatch(config: { $position: ResolvedPos }): DueDateSuggestionMatch | null {
  const { $position } = config;
  const { nodeBefore } = $position;

  if (!nodeBefore) return null;

  if (isPlainDoubleMention(nodeBefore)) {
    // In this case we've just opened up the suggestion dropdown but haven't entered any text yet.
    const pos = $position.pos;

    return {
      // Range covers zero characters at the cursor position (before the mention node)
      range: { from: pos, to: pos },
      query: "", // No query text (user hasn’t typed anything for this trigger)
      text: "", // No trigger character or text
      mention: {
        node: nodeBefore,
        range: getNodeRange(pos - 1, nodeBefore),
      },
    };
  }

  if (!nodeBefore.isText) return null;

  const nodeBeforeNodeAtCurrentPosition = findNodeBeforeNodeAtCurrentPosition($position);

  if (!nodeBeforeNodeAtCurrentPosition) return null;

  if (!isPlainDoubleMention(nodeBeforeNodeAtCurrentPosition)) {
    return null;
  }

  const beforeText = nodeBefore.text;

  if (!beforeText || beforeText.startsWith(" ")) {
    return null;
  }

  const startPos = $position.pos - beforeText.length;
  const endPos = $position.pos;

  // The absolute position of the match in the document
  const from = startPos;
  const to = endPos;

  // If the $position is located within the matched substring, return that range
  if (from < $position.pos && to >= $position.pos) {
    return {
      range: {
        from,
        to,
      },
      query: beforeText,
      text: beforeText,
      mention: {
        node: nodeBeforeNodeAtCurrentPosition,
        range: getNodeRange(from - 1, nodeBeforeNodeAtCurrentPosition),
      },
    };
  }

  return null;
}

// This function receives a ResolvedPos argument and finds the nodeBefore.
// If the current position is in the middle of a node, then the nodeBefore
// and nodeAfter will be slices of the same node. In this case, we want to
// find and return the node before the current one in the parent node. Else,
// we just return the nodeBefore.
function findNodeBeforeNodeAtCurrentPosition($position: ResolvedPos) {
  // If there is no before node, return null
  if (!$position.nodeBefore) return null;

  const posRelativeToParent = $position.parentOffset - $position.nodeBefore.nodeSize;

  return $position.parent.childBefore(posRelativeToParent).node;
}

/**
 * Here a "plain" double mention is one that isn't marked complete and which
 * doesn't have a due date.
 */
function isPlainDoubleMention(node: Node) {
  if (node.type.name !== "mention") return false;
  const attrs = node.attrs as IMentionAttributes;
  if (String(attrs.priority) !== "200") return false;
  if (attrs.requestComplete && String(attrs.requestComplete) !== "false") return false;
  return !attrs.dueDate;
}

function getNodeRange(startPos: number, node: Node) {
  return {
    from: startPos,
    to: startPos + node.nodeSize,
  };
}
